import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { httpClient } from '../api/http';
import { queryParamBuilder } from '../helpers/utils';
import {
  DEFAULT_IPO_PAGE_SIZE,
  IPO_OFFER_LOOKUP_PAGE_SIZE,
  defaultIpoPagination,
  lookupIpoOfferSummary,
  mapIpoSubscriptionDetailPayload,
  parseIpoInvestor,
  parseIpoOfferSubscriptions,
  parseIpoUserInvestments,
} from '../helpers/utils/investify';
import { IpoSubscriptionDetailResponse, UseIpoOptions } from '../types';

const fetchUserInvestments = (userId: string, cursor?: string | number | null, limit = DEFAULT_IPO_PAGE_SIZE) =>
  httpClient
    .get(
      `admin/investify/public-offers/users/${userId}/investments` +
        queryParamBuilder({
          cursor,
          limit,
        }),
    )
    .then((res) => parseIpoUserInvestments(res.data));

const useIpo = (options: UseIpoOptions = {}) => {
  const { id } = useParams();
  const userId = id as string;
  const page = options.page || 1;
  const limit = options.limit || DEFAULT_IPO_PAGE_SIZE;
  const offerId = options.offerId;

  const offerSubscriptionsQuery = useQuery({
    queryKey: ['ipo-offer-subscriptions', userId, offerId, page, limit],
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !!userId && !!offerId && options.enabled !== false,
    queryFn: () =>
      httpClient
        .get(
          `admin/investify/public-offers/users/${userId}/investments/${offerId}/subscriptions` +
            queryParamBuilder({
              page,
              limit,
            }),
        )
        .then((res) => parseIpoOfferSubscriptions(res.data, page, limit)),
  });

  const detailsQuery = useQuery({
    queryKey: ['ipo-subscription-details', options.subscriptionId],
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !!options.subscriptionId,
    queryFn: () =>
      httpClient
        .get<IpoSubscriptionDetailResponse>(`admin/investify/public-offers/subscriptions/${options.subscriptionId}`)
        .then((res) => mapIpoSubscriptionDetailPayload(res.data?.data)),
  });

  const investorQuery = useQuery({
    queryKey: ['ipo-investor', userId],
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !!userId && options.fetchInvestor === true,
    queryFn: () =>
      httpClient
        .get(`admin/investify/public-offers/users/${userId}/investor`, { hideError: true })
        .then((res) => parseIpoInvestor(res.data)),
  });

  const investmentLimit = options.investmentLimit || DEFAULT_IPO_PAGE_SIZE;
  const investmentsQuery = useQuery({
    queryKey: ['ipo-user-investments', userId, options.cursor, investmentLimit],
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !!userId && options.fetchInvestments === true,
    queryFn: () => fetchUserInvestments(userId, options.cursor, investmentLimit),
  });

  const offerSummaryQuery = useQuery({
    queryKey: ['ipo-offer-summary', userId, offerId],
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !!userId && !!offerId && options.fetchOfferSummary === true,
    queryFn: () =>
      lookupIpoOfferSummary(offerId as string, (cursor) =>
        fetchUserInvestments(userId, cursor, IPO_OFFER_LOOKUP_PAGE_SIZE),
      ),
  });

  return {
    isLoading: offerSubscriptionsQuery.isLoading,
    fetchingDetails: detailsQuery.isLoading,
    subscriptions: offerSubscriptionsQuery.data?.subscriptions || [],
    subscriptionDetails: detailsQuery.data || null,
    investor: investorQuery.data || null,
    investments: investmentsQuery.data?.investments || [],
    offerSummary: offerSummaryQuery.data || null,
    investmentsHasNextPage: Boolean(investmentsQuery.data?.hasNextPage),
    investmentsEndCursor: investmentsQuery.data?.endCursor ?? null,
    isLoadingInvestments: investmentsQuery.isLoading,
    pagination: offerSubscriptionsQuery.data?.pagination || defaultIpoPagination(page, limit),
    subscriptionsHasNextPage: Boolean(offerSubscriptionsQuery.data?.hasNextPage),
  };
};

export default useIpo;
